import {
  Descriptions as AntdDescriptions,
  Col,
  Collapse,
  Row,
  Alert,
  Space,
  Tag as AntdTag,
} from "antd";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import Descriptions from "../../general/antd/descriptions";
import PageDetails from "../../general/details";
import { useNavigate, useParams, Link } from "react-router-dom";
import Spin from "../../general/antd/spin";
import AppContext from "../../../context/app/context";
import OrderContext from "../../../context/order/context";
import OrderContextProvider from "../../../context/order/provider";
import Tag from "../../general/antd/tag";
import {
  OrderStatus,
  getOrderStatus,
  getOrderStatusColor,
  getOrderStyle,
  getOrderTypeColor,
  getorderSchedulingTypeStyle,
  getorderSchedulingTypeColor,
  getOrderKindName,
  getOrderKindColor,
  getDeliveryModeName,
  getDeliveryModeColor,
} from "../../../models/order/enum";
import { IOrderDetails, TripStatus } from "../../../models/order/response";
import { durationBetweenDatesByDays } from "../../../utils/helpers/functions";
import { ImCheckmark, ImCross } from "react-icons/im";
import OrderStatusContextProvider from "../../../context/order-status/provider";
import OrderHistoryContextProvider from "../../../context/order-history/provider";
import OrderHistoryTable from "../order-history-table";
import OrderStatusTable from "../order-status-table";
import moment from "moment";
import OldImage from "../../form-components/old-image";
import ProductsOrderTable from "../products-order";
import RefreshBtn from "../../general/table-components/actions/refresh-btn";
import WorkerHistoryTable from "../../delivery-worker/delivery-worker-history";
import DeliveryWorkerHistoryContextProvider from "../../../context/delivery-worker-history/provider";
import Marker from "../../general/map/marker";
import StoreContextProvider from "../../../context/store/provider";
import { Button, Tooltip, Modal, theme, Input } from "antd";
import {
  PlusOutlined,
  CheckOutlined,
  WarningOutlined,
  EyeOutlined,
  EnvironmentOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import { Permissions } from "../../../utils/Permissions/Permissions";
import { successNotification } from "../../../utils/helpers/notification";
import { isLocationChanged } from "../../../utils/helpers/order.helpers";
import TripBadge from "../trip-badge";
import {
  getOrderTripView,
  getTripStatusColor,
  getTripStatusLabel,
} from "../../../utils/helpers/trip-helpers";
import CustomerSatisfactionPanel from "../customer-satisfaction-panel";
import OrderCashCheckPanel, { ICashCheckGate } from "../order-cash-check-panel";
import ReturnToCashModal from "../return-to-cash-modal";
import { CashCheckErrorKey } from "../../../models/order/cash-check";
import CustomerSatisfactionStatusTag from "../customer-satisfaction-status-tag";
import OrderOperationTimingsPanel from "../order-operation-timings-panel";
import OrderOperationDuration from "../order-operation-duration";
import { formatPersonName } from "../../../utils/helpers/person";
import EndPoints from "../../../services/end-points";
import {
  IReturnToCashRequest,
  RETURN_TO_CASH_TRIP_ACTION_TRANSLATION_KEYS,
  ReturnToCashErrorKey,
} from "../../../models/order/return-to-cash";
import styles from "./style.module.scss";

const { Panel } = Collapse;
const { useToken } = theme;
interface Props {}

const Order: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { token } = useToken();
  const { direction, screenSize } = useContext(AppContext);
  const { details, actions, loading } = useContext(OrderContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const [isRefreshed, setIsRefreshed] = useState(false);
  const userPermissions =
    localStorage.getItem("permisionStoroge")?.split(",") || [];
  const canViewOrderCashCheck = userPermissions.includes(
    Permissions.CAN_VIEW_ORDER_CASH_CHECK,
  );
  const canReturnOrderToCash = userPermissions.includes(
    Permissions.CAN_RETURN_ORDER_TO_CASH,
  );
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [accountingModalVisible, setAccountingModalVisible] = useState(false);
  const [accountingFinishNote, setAccountingFinishNote] = useState("");
  const cashCheckGateScope = `${id ?? ""}:${details?.id ?? ""}:${
    details?.status ?? ""
  }`;
  const [cashCheckGateSnapshot, setCashCheckGateSnapshot] = useState<{
    scope: string;
    gate: ICashCheckGate | null;
  }>({ scope: "", gate: null });
  // A gate reported for a previous order/status is unknown for the current
  // lifecycle and therefore remains fail-closed until the mounted Cash panel
  // reports its state. Keying the snapshot avoids the parent/child effect race
  // that used to overwrite a freshly reported gate with null on first load.
  const cashCheckGate =
    cashCheckGateSnapshot.scope === cashCheckGateScope
      ? cashCheckGateSnapshot.gate
      : null;
  const setCashCheckGate = useCallback(
    (gate: ICashCheckGate | null) => {
      setCashCheckGateSnapshot({ scope: cashCheckGateScope, gate });
    },
    [cashCheckGateScope],
  );
  const [returnToCashOpen, setReturnToCashOpen] = useState(false);
  const [returnToCashLoading, setReturnToCashLoading] = useState(false);
  // Drives the shortage badge on weighted product lines. Kept null until the
  // setting arrives so the table falls back to the shared default instead of
  // flashing a badge computed from a zero tolerance.
  const [weightedToleranceGrams, setWeightedToleranceGrams] = useState<
    number | null
  >(null);

  const currentOrderIdRef = useRef(id);
  currentOrderIdRef.current = id;
  const orderActionsRef = useRef({
    getDetails: actions.getDetails,
    setDetails: actions.setDetails,
  });
  useEffect(() => {
    orderActionsRef.current = {
      getDetails: actions.getDetails,
      setDetails: actions.setDetails,
    };
  }, [actions.getDetails, actions.setDetails]);

  const refreshOrderDetailsSilently = useCallback(
    async (
      suppressErrorNotification = false,
    ): Promise<IOrderDetails | undefined> => {
      if (!id) return undefined;

      const requestedOrderId = Number(id);
      const { data } = await EndPoints.order.getOrder(
        requestedOrderId,
        canViewOrderCashCheck ? { include: "cashCheck" } : undefined,
        suppressErrorNotification
          ? { suppressErrorNotification: true }
          : undefined,
      );
      if (Number(currentOrderIdRef.current) !== requestedOrderId) {
        return undefined;
      }

      orderActionsRef.current.setDetails(data);
      return data;
    },
    [canViewOrderCashCheck, id],
  );

  const refreshCashCheckSnapshot = useCallback(async () => {
    const refreshed = await refreshOrderDetailsSilently();
    return refreshed?.cashCheck ?? null;
  }, [refreshOrderDetailsSilently]);

  // Check if order is in an active trip (PENDING or ACTIVE status)
  const isInActiveTrip = useMemo(() => {
    if (!details?.tripId || !details?.tripStatus) return false;
    return (
      details.tripStatus === TripStatus.PENDING ||
      details.tripStatus === TripStatus.ACTIVE
    );
  }, [details?.tripId, details?.tripStatus]);
  const isTripActive = details?.tripStatus === TripStatus.ACTIVE;

  // The trip to display. Unlike the fields above it stays populated once the
  // trip has ended, so it is the only thing that can show which trip delivered
  // the order. It never feeds the protection logic above.
  const tripView = useMemo(() => getOrderTripView(details), [details]);
  const showTripHistory = !!tripView && !tripView.isCurrent;

  const canReturnToCash =
    canReturnOrderToCash &&
    details?.status === OrderStatus.SEARCHING_FOR_DELIVERY_DRIVER;

  const isCashStage = details?.status === OrderStatus.ACCOUNTING_FINALIZATION;
  const canMarkAccountingFinished =
    isCashStage &&
    details?.canMarkAccountingFinished === true &&
    !details?.accountingFinishedAt;
  const isCashCheckConfirming =
    canViewOrderCashCheck &&
    isCashStage &&
    (cashCheckGate === null || cashCheckGate.isBusy);
  const isCashCheckBlocking =
    canViewOrderCashCheck &&
    isCashStage &&
    (cashCheckGate === null || cashCheckGate.isBlocked);
  const hasLocationChanged = isLocationChanged(details?.locationChanged);
  useEffect(() => {
    if (!id) return;
    void orderActionsRef.current.getDetails(Number(id), canViewOrderCashCheck);
  }, [canViewOrderCashCheck, direction, id, isRefreshed]);

  useEffect(() => {
    let isActive = true;

    const getWeightedTolerance = async () => {
      try {
        const { data } = await EndPoints.generalSettings.getGeneralSetting();
        if (!isActive) return;
        setWeightedToleranceGrams(data?.weightedProductToleranceGrams ?? null);
      } catch {
        // A missing setting only costs the badge its exact threshold; the table
        // falls back to the shared default rather than breaking the screen.
      }
    };

    void getWeightedTolerance();

    return () => {
      isActive = false;
    };
  }, []);
  const handleReturnToCash = useCallback(
    async (request: IReturnToCashRequest) => {
      if (!id || returnToCashLoading) return;

      setReturnToCashLoading(true);
      try {
        const { data } = await EndPoints.order.returnToCash(
          Number(id),
          request,
        );
        setCashCheckGate(null);

        let refreshedOrder: IOrderDetails | undefined;
        if (canViewOrderCashCheck) {
          try {
            refreshedOrder = await refreshOrderDetailsSilently(true);
          } catch {
            // Return-to-Cash already succeeded. The mounted Cash panel will
            // retry the authoritative snapshot without replaying the action.
          }
        }

        orderActionsRef.current.setDetails(refreshedOrder ?? data.order);
        setReturnToCashOpen(false);
        successNotification(
          t(
            RETURN_TO_CASH_TRIP_ACTION_TRANSLATION_KEYS[data.tripAction] ??
              "return_to_cash_success",
          ),
        );
      } catch (error: any) {
        const errorKey = error?.response?.data?.errorKey as
          | ReturnToCashErrorKey
          | undefined;

        if (
          errorKey === ReturnToCashErrorKey.INVALID_STATUS ||
          errorKey === ReturnToCashErrorKey.ACTIVE_TRIP ||
          errorKey === ReturnToCashErrorKey.TRIP_CHANGED_RETRY
        ) {
          let refreshedOrder: IOrderDetails | undefined;
          try {
            refreshedOrder = await refreshOrderDetailsSilently(true);
          } catch {
            // Keep the server error as the only notification.
          }

          if (
            errorKey !== ReturnToCashErrorKey.TRIP_CHANGED_RETRY ||
            (refreshedOrder &&
              refreshedOrder.status !==
                OrderStatus.SEARCHING_FOR_DELIVERY_DRIVER)
          ) {
            setReturnToCashOpen(false);
          }
        }

        throw error;
      } finally {
        setReturnToCashLoading(false);
      }
    },
    [
      canViewOrderCashCheck,
      id,
      refreshOrderDetailsSilently,
      returnToCashLoading,
      t,
    ],
  );

  return (
    <>
      <PageDetails
        title={t("details")}
        subtitle={t("request")}
        buttonName={`${t("update_order")}`}
        onClick={() => navigate(`/orders/update/${id}`)}
        loading={loading.includes("details")}
        onRefresh={() =>
          orderActionsRef.current.getDetails(Number(id), canViewOrderCashCheck)
        }
        headerExtra={
          details?.number ? (
            <div className={styles.orderHeaderNumber}>
              <span className={styles.orderHeaderNumberLabel}>
                {t("order_number")}
              </span>
              <span className={styles.orderHeaderNumberValue}>
                {details.number}
              </span>
            </div>
          ) : undefined
        }
      >
        {loading.includes("details") ? (
          <Spin />
        ) : (
          <>
            {/* Order Protection Warning Banner */}
            {isInActiveTrip && (
              <Alert
                message={t("order_in_active_trip")}
                description={
                  <Space direction="vertical" size="small">
                    <span>{t("order_in_active_trip_warning")}</span>
                    <Space>
                      <span style={{ fontWeight: 500 }}>
                        {t("trip_id")}: {tripView?.number || details?.tripId}
                      </span>
                      <Link to={`/trips/details/${details?.tripId}`}>
                        <Button
                          type="primary"
                          size="small"
                          icon={<EyeOutlined />}
                        >
                          {t("view_trip")}
                        </Button>
                      </Link>
                    </Space>
                  </Space>
                }
                type="warning"
                showIcon
                icon={<WarningOutlined />}
                style={{ marginBottom: 16 }}
              />
            )}

            {/* Historical trip record - the trip is over (or the order was
                pulled out of it), so this is a read-only account of which trip
                carried the order, with no trip controls. */}
            {showTripHistory && tripView && (
              <Alert
                message={
                  tripView.removedAt
                    ? t("order_removed_from_trip")
                    : t("order_was_in_trip")
                }
                description={
                  <Space direction="vertical" size="small">
                    <Space wrap>
                      <span style={{ fontWeight: 500 }}>
                        {t("trip_id")}: {tripView.number || `#${tripView.id}`}
                      </span>
                      <Tag
                        title={getTripStatusLabel(tripView.status)}
                        color={getTripStatusColor(tripView.status)}
                      />
                      {tripView.wasSkipped && (
                        <Tag title={t("skipped")} color="warning" />
                      )}
                    </Space>
                    {tripView.driverName && (
                      <span>
                        {t("driver")}: {tripView.driverName}
                      </span>
                    )}
                    {tripView.deliveredAt && (
                      <span>
                        {t("order_delivered_at")}:{" "}
                        {moment(tripView.deliveredAt).format(
                          "YYYY-MM-DD HH:mm:ss",
                        )}
                      </span>
                    )}
                    {tripView.sequence != null && (
                      <span>
                        {t("sequence")}: {tripView.sequence}
                      </span>
                    )}
                    {tripView.removedAt && (
                      <span>
                        {t("removed_at")}:{" "}
                        {moment(tripView.removedAt).format(
                          "YYYY-MM-DD HH:mm:ss",
                        )}
                        {tripView.removalReason
                          ? ` - ${t("removal_reason")}: ${tripView.removalReason}`
                          : ""}
                      </span>
                    )}
                    <Link to={`/trips/details/${tripView.id}`}>
                      <Button size="small" icon={<EyeOutlined />}>
                        {t("view_trip")}
                      </Button>
                    </Link>
                  </Space>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {/* Location Changed Alert */}
            {hasLocationChanged && (
              <Alert
                message={t("location_changed")}
                description={
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    <span>{t("location_changed_description")}</span>
                    <Descriptions
                      size="small"
                      column={screenSize === "laptopOrDesktop" ? 2 : 1}
                      bordered
                    >
                      <AntdDescriptions.Item label={t("browsing_store")}>
                        {details?.browsingStore?.name ?? "-"}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("delivery_store")}>
                        {details?.deliveryStore?.name ?? "-"}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("browsing_total_price")}>
                        {details?.browsingTotalPrice?.toLocaleString() ?? "-"}{" "}
                        {t("currency")}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("delivery_total_price")}>
                        {details?.deliveryTotalPrice?.toLocaleString() ?? "-"}{" "}
                        {t("currency")}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item
                        label={t("price_difference")}
                        span={2}
                      >
                        <span
                          style={{
                            color:
                              (details?.priceDifference ?? 0) > 0
                                ? token.colorError
                                : token.colorSuccess,
                            fontWeight: 600,
                            fontSize: "16px",
                          }}
                        >
                          {(details?.priceDifference ?? 0) > 0 ? "+" : ""}
                          {details?.priceDifference?.toLocaleString() ??
                            "0"}{" "}
                          {t("currency")}
                        </span>
                      </AntdDescriptions.Item>
                    </Descriptions>
                  </div>
                }
                type="warning"
                showIcon
                icon={<EnvironmentOutlined />}
                style={{ marginBottom: 16 }}
              />
            )}
            <Row gutter={[8, 8]}>
              <Col span={24}>
                <Collapse>
                  <Panel
                    header={
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 8,
                          width: "100%",
                        }}
                      >
                        <span>{t("details")}</span>
                        <Space size="small" wrap>
                          <Tag
                            title={getOrderStatus(details?.status)}
                            color={getOrderStatusColor(details?.status)}
                          />
                          <Tag
                            title={getOrderKindName(details?.isExpressDelivery)}
                            color={getOrderKindColor(
                              details?.isExpressDelivery,
                            )}
                          />
                          {canMarkAccountingFinished && (
                            <Tooltip
                              title={
                                isCashCheckConfirming
                                  ? t("cash_check_confirming")
                                  : isCashCheckBlocking
                                    ? t("cash_check_finish_blocked")
                                    : undefined
                              }
                            >
                              <Button
                                type="primary"
                                size="small"
                                icon={<CheckOutlined />}
                                disabled={isCashCheckBlocking}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAccountingModalVisible(true);
                                }}
                                loading={
                                  cashCheckGate?.isBusy === true ||
                                  loading.includes("mark_accounting_finished")
                                }
                              >
                                {t("finish_accounting_finalization")}
                              </Button>
                            </Tooltip>
                          )}
                          {canReturnToCash && (
                            <Tooltip
                              title={
                                isTripActive
                                  ? t("return_to_cash_active_trip_blocked")
                                  : undefined
                              }
                            >
                              <span>
                                <Button
                                  danger
                                  type="default"
                                  size="small"
                                  icon={<RollbackOutlined />}
                                  disabled={isTripActive}
                                  loading={returnToCashLoading}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setReturnToCashOpen(true);
                                  }}
                                >
                                  {t("return_to_cash_confirm")}
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                          {userPermissions.includes(
                            Permissions.CAN_CONFIRM_ORDER_PAYMENT,
                          ) &&
                            details?.status === 8 &&
                            !details?.paymentReceivedBy && (
                              <Button
                                type="primary"
                                size="small"
                                icon={<CheckOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConfirmModalVisible(true);
                                }}
                                loading={loading.includes("confirm_payment")}
                              >
                                {t("confirm_payment_receipt")}
                              </Button>
                            )}
                        </Space>
                      </div>
                    }
                    key={1}
                  >
                    <Descriptions
                      size="small"
                      column={screenSize == "laptopOrDesktop" ? 2 : 1}
                      bordered
                    >
                      <AntdDescriptions.Item label={t("id")}>
                        {details?.id}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("number_id")}>
                        {details?.number}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("is_express_delivery")}>
                        {
                          <Tag
                            title={t(
                              getOrderKindName(details?.isExpressDelivery),
                            )}
                            color={getOrderKindColor(
                              details?.isExpressDelivery,
                            )}
                          />
                        }
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("quantityProcessed")}>
                        {details?.quantityProcessed ? (
                          <ImCheckmark color="green" />
                        ) : (
                          <ImCross color="red" />
                        )}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("client")}>
                        <Space size="small" wrap>
                          <span>{formatPersonName(details?.client)}</span>
                          {details?.client?.id && (
                            <Link to={`/clients/details/${details.client.id}`}>
                              <Button
                                type="link"
                                size="small"
                                icon={<EyeOutlined />}
                                style={{ paddingInline: 0 }}
                              >
                                {t("view_client")}
                              </Button>
                            </Link>
                          )}
                        </Space>
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("client_mobile")}>
                        {details?.client?.cleanMobile ?? "-"}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("client_address")}>
                        {details?.clientAddress ?? "-"}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item
                        label={t("last_completed_order_date")}
                      >
                        {!details?.client?.lastCompletedOrderDate
                          ? "-"
                          : moment(
                              details?.client?.lastCompletedOrderDate,
                            ).format("YYYY-MM-DD HH:mm:ss")}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("days_form_last_order")}>
                        {!details?.client?.lastCompletedOrderDate
                          ? "-"
                          : durationBetweenDatesByDays(
                              moment(details?.client?.lastCompletedOrderDate),
                              moment(details?.createdAt),
                            )}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("delivery_worker")}>
                        {formatPersonName(details?.driver)}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item
                        label={t("delivery_worker_mobile")}
                      >
                        {details?.driver?.cleanMobile ?? "-"}
                      </AntdDescriptions.Item>

                      <AntdDescriptions.Item label={t("investor")}>
                        {formatPersonName(details?.investor)}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("store")}>
                        {details?.store?.name ?? "-"}
                      </AntdDescriptions.Item>

                      <AntdDescriptions.Item label={t("store_have_recipe")}>
                        {details?.StoreHaveRecipe?.name ?? "-"}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("order_scheduling_type")}>
                        {
                          <Tag
                            title={getorderSchedulingTypeStyle(
                              details?.orderSchedulingType,
                            )}
                            color={getorderSchedulingTypeColor(
                              details?.orderSchedulingType,
                            )}
                          />
                        }
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("schedule_time")}>
                        {details?.scheduleTime
                          ? moment(details?.scheduleTime).format(
                              "YYYY-MM-DD HH:mm:ss",
                            )
                          : "--"}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("request_sourse_type")}>
                        {
                          <Tag
                            title={getOrderStyle(details?.type)}
                            color={getOrderTypeColor(details?.type)}
                          />
                        }
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("current_order_status")}>
                        {
                          <Tag
                            title={getOrderStatus(details?.status)}
                            color={getOrderStatusColor(details?.status)}
                          />
                        }
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("trip_info")}>
                        <TripBadge trip={tripView} />
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("delivery_mode")}>
                        <Tag
                          title={t(getDeliveryModeName(details?.deliveryMode))}
                          color={getDeliveryModeColor(details?.deliveryMode)}
                        />
                      </AntdDescriptions.Item>

                      <AntdDescriptions.Item label={t("date_and_time")}>
                        {moment(details?.createdAt).format(
                          "YYYY-MM-DD HH:mm:ss",
                        )}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("final_price")}>
                        {details?.finalPrice ?? "-"}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("reason_cancel")}>
                        {details?.reasonCancel ?? "-"}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("emergency")}>
                        {details?.isEmergency ? (
                          <ImCheckmark color="green" />
                        ) : (
                          <ImCross color="red" />
                        )}
                      </AntdDescriptions.Item>

                      <AntdDescriptions.Item label={t("order_broadcast_area")}>
                        {details?.orderBroadcastArea ? (
                          <ImCheckmark color="green" />
                        ) : (
                          <ImCross color="red" />
                        )}
                      </AntdDescriptions.Item>

                      <AntdDescriptions.Item label={t("order_broadcast_city")}>
                        {details?.orderBroadcastCity ? (
                          <ImCheckmark color="green" />
                        ) : (
                          <ImCross color="red" />
                        )}
                      </AntdDescriptions.Item>

                      <AntdDescriptions.Item
                        label={t("product_broadcast_area")}
                      >
                        {details?.productBroadcastArea ? (
                          <ImCheckmark color="green" />
                        ) : (
                          <ImCross color="red" />
                        )}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item
                        label={t("product_broadcast_city")}
                      >
                        {details?.productBroadcastCity ? (
                          <ImCheckmark color="green" />
                        ) : (
                          <ImCross color="red" />
                        )}
                      </AntdDescriptions.Item>

                      <AntdDescriptions.Item label={t("is_recipe")}>
                        {details?.isRecipe ? (
                          <ImCheckmark color="green" />
                        ) : (
                          <ImCross color="red" />
                        )}
                      </AntdDescriptions.Item>

                      <AntdDescriptions.Item label={t("do_not_contact")}>
                        {!details?.contact ? (
                          <ImCheckmark color="green" />
                        ) : (
                          <ImCross color="red" />
                        )}
                      </AntdDescriptions.Item>

                      <AntdDescriptions.Item label={t("not_ringing_bell")}>
                        {!details?.ringingBell ? (
                          <ImCheckmark color="green" />
                        ) : (
                          <ImCross color="red" />
                        )}
                      </AntdDescriptions.Item>

                      <AntdDescriptions.Item label={t("expanded_search")}>
                        {details?.selectedStore ? (
                          <ImCross color="red" />
                        ) : (
                          <ImCheckmark color="green" />
                        )}
                      </AntdDescriptions.Item>

                      <AntdDescriptions.Item
                        label={t("price_of_products_from_pharmacy")}
                      >
                        {details?.totalPrice}
                      </AntdDescriptions.Item>

                      <AntdDescriptions.Item label={t("total_price_on_client")}>
                        {details?.totalPriceOnClient}
                      </AntdDescriptions.Item>

                      <AntdDescriptions.Item label={t("products_yield")}>
                        {details?.productYield}
                      </AntdDescriptions.Item>

                      <AntdDescriptions.Item
                        label={t("price_delivery_service")}
                      >
                        {details?.priceDeliveryService}
                      </AntdDescriptions.Item>

                      <AntdDescriptions.Item
                        label={t("price_delivery_Service_on_client")}
                      >
                        {details?.priceDeliveryServiceOnClient}
                      </AntdDescriptions.Item>

                      <AntdDescriptions.Item label={t("price_delivery_yield")}>
                        {details?.priceDeliveryYield}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("discountCode")}>
                        {details?.discountCode?.code}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("discountValueProduct")}>
                        {details?.discountCodeValueForProduct}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("discountValueDelivery")}>
                        {details?.discountCodeValueForDelivery}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("distance")}>
                        {details?.distance}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item
                        label={t("applicable_discount_code")}
                      >
                        {details?.discountCode?.name}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item
                        label={t("is_subscription_coupon")}
                      >
                        {details?.isSubscriptionCoupon ? (
                          <ImCheckmark color="green" />
                        ) : (
                          <ImCross color="red" />
                        )}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("finish_date_time")}>
                        {details?.finishDatetime
                          ? moment(details?.finishDatetime).format(
                              "YYYY-MM-DD HH:mm:ss",
                            )
                          : "--"}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("start_datetime_driver")}>
                        {details?.startDatetimeDriver
                          ? moment(details?.startDatetimeDriver).format(
                              "YYYY-MM-DD HH:mm:ss",
                            )
                          : "--"}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("recipe_as_text")}>
                        {details?.recipeAsText ?? "-"}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item
                        label={t("recipes_images")}
                        span={2}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-start",
                            gap: "7px",
                          }}
                        >
                          {details?.recipes?.map((img) => {
                            return <OldImage src={img?.url} preview />;
                          })}
                        </div>
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item
                        label={t("customer_evaluation_of_the_service")}
                        span={2}
                      >
                        {details?.rating}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item
                        label={t("client_rate_note")}
                        span={2}
                      >
                        {details?.clientRateNote}
                      </AntdDescriptions.Item>

                      <AntdDescriptions.Item
                        label={t("client_note_additional")}
                        span={2}
                      >
                        {details?.clientNote}
                      </AntdDescriptions.Item>

                      <AntdDescriptions.Item
                        label={t("client_note_delivery")}
                        span={2}
                      >
                        {details?.clientNoteDelivery}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item label={t("order_notes")} span={2}>
                        {details?.notes?.map((note, index) => (
                          <div key={note?.id}>
                            <span> {index + 1}-</span> {note?.text} : ({" "}
                            {note?.noteForNote} ).
                          </div>
                        )) ?? "-"}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item
                        label={t("managment_note")}
                        span={2}
                      >
                        {details?.note}
                      </AntdDescriptions.Item>

                      {/* Payment Confirmation Section */}
                      <AntdDescriptions.Item
                        label={t("payment_status")}
                        span={2}
                      >
                        {details?.paymentReceivedBy ? (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                              padding: "12px",
                              backgroundColor: token.colorSuccessBg,
                              border: `1px solid ${token.colorSuccessBorder}`,
                              borderRadius: token.borderRadius,
                            }}
                          >
                            <Tag title={t("payment_received")} color="green" />
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "4px",
                                marginTop: "8px",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "14px",
                                  fontWeight: "500",
                                  color: token.colorText,
                                }}
                              >
                                <strong>
                                  {t("payment_received_by_accountant")}:
                                </strong>{" "}
                                {formatPersonName(details.paymentReceivedBy)}
                              </span>
                              <span
                                style={{
                                  fontSize: "14px",
                                  color: token.colorTextSecondary,
                                  unicodeBidi: "plaintext",
                                }}
                              >
                                <strong>{t("payment_received_at")}:</strong>{" "}
                                {moment(details.paymentReceivedAt).format(
                                  "YYYY-MM-DD HH:mm:ss",
                                )}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <Tag title={t("payment_not_received")} color="gray" />
                        )}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item
                        label={t("accounting_finished_at")}
                      >
                        {details?.accountingFinishedAt
                          ? moment(details.accountingFinishedAt).format(
                              "YYYY-MM-DD HH:mm:ss",
                            )
                          : "--"}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item
                        label={t("accounting_finished_by")}
                      >
                        {details?.accountingFinishedBy
                          ? formatPersonName(details.accountingFinishedBy)
                          : "--"}
                      </AntdDescriptions.Item>
                      <AntdDescriptions.Item
                        label={t("accounting_finish_note")}
                        span={2}
                      >
                        {details?.accountingFinishNote || "--"}
                      </AntdDescriptions.Item>
                    </Descriptions>
                  </Panel>
                </Collapse>
              </Col>
              <Col span={24}>
                <Collapse>
                  <Panel
                    header={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span>{t("operation_timings")}</span>
                        <AntdTag
                          color={details?.operationTimings ? "blue" : "gray"}
                        >
                          {details?.operationTimings ? (
                            <OrderOperationDuration
                              duration={
                                details.operationTimings.totalCycleDuration
                              }
                              seconds={
                                details.operationTimings.totalCycleSeconds
                              }
                            />
                          ) : (
                            t("no_operation_timings")
                          )}
                        </AntdTag>
                      </div>
                    }
                    key={1}
                  >
                    <OrderOperationTimingsPanel
                      timings={details?.operationTimings}
                    />
                  </Panel>
                </Collapse>
              </Col>
              {userPermissions.includes(
                Permissions.CAN_VIEW_ORDER_CUSTOMER_SATISFACTIONS,
              ) && (
                <Col span={24}>
                  <Collapse>
                    <Panel
                      header={
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <span>{t("customer_satisfaction")}</span>
                          <CustomerSatisfactionStatusTag
                            status={details?.customerSatisfactionStatus}
                            fallbackStatus={
                              details?.customerSatisfaction?.status
                            }
                          />
                        </div>
                      }
                      key={1}
                    >
                      <CustomerSatisfactionPanel
                        orderId={Number(id)}
                        customerSatisfaction={details?.customerSatisfaction}
                        disabled={
                          !userPermissions.includes(
                            Permissions.CAN_UPDATE_ORDER_CUSTOMER_SATISFACTION,
                          )
                        }
                        canViewAudits={userPermissions.includes(
                          Permissions.CAN_VIEW_ORDER_CUSTOMER_SATISFACTION_AUDITS,
                        )}
                        saving={loading.includes("customer_satisfaction")}
                        onSave={async (data) => {
                          await actions.updateCustomerSatisfaction(
                            Number(id),
                            data,
                          );
                        }}
                      />
                    </Panel>
                  </Collapse>
                </Col>
              )}
              <Col span={24}>
                <Collapse>
                  <Panel header={t("location")} key={1}>
                    <Marker
                      width="100%"
                      name={formatPersonName(details?.client)}
                      draggable={false}
                      location={details?.location}
                    />
                  </Panel>
                </Collapse>
              </Col>
              {canViewOrderCashCheck && details?.id && (
                <Col span={24}>
                  <OrderCashCheckPanel
                    orderId={Number(id)}
                    key={Number(id)}
                    isCashStage={isCashStage}
                    orderProducts={details?.orderProducts}
                    initialCheck={details?.cashCheck}
                    canFinishCash={canMarkAccountingFinished}
                    finishing={loading.includes("mark_accounting_finished")}
                    onFinishCash={() => setAccountingModalVisible(true)}
                    onAuthoritativeRefresh={refreshCashCheckSnapshot}
                    onGateChange={setCashCheckGate}
                  />
                </Col>
              )}
              <Col span={24}>
                <Collapse defaultActiveKey={1}>
                  <Panel
                    header={
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>{t("products_order")}</span>
                        {userPermissions.includes(
                          Permissions.CAN_ADMIN_ADD_ORDER_PRODUCT,
                        ) && (
                          <Tooltip
                            title={
                              isInActiveTrip
                                ? t("cannot_modify_order_in_trip")
                                : undefined
                            }
                          >
                            <Button
                              type="primary"
                              size="small"
                              icon={<PlusOutlined />}
                              disabled={isInActiveTrip}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isInActiveTrip) {
                                  navigate(`/orders/add-products/${id}`, {
                                    state: {
                                      orderNumber: details?.number,
                                      storeId: details?.store?.id,
                                      existingProductIds:
                                        details?.orderProducts?.map(
                                          (p: any) => p.product.id,
                                        ) || [],
                                    },
                                  });
                                }
                              }}
                              aria-label={t("add_product")}
                            >
                              {t("add_product")}
                            </Button>
                          </Tooltip>
                        )}
                      </div>
                    }
                    key={1}
                  >
                    <ProductsOrderTable
                      data={details?.orderProducts}
                      orderId={Number(id)}
                      disableMutations={isInActiveTrip}
                      weightedToleranceGrams={weightedToleranceGrams}
                      cashCheckLines={details?.cashCheck?.lines}
                      onRefresh={() =>
                        orderActionsRef.current.getDetails(
                          Number(id),
                          canViewOrderCashCheck,
                        )
                      }
                    />
                  </Panel>
                </Collapse>
              </Col>

              <Col span={24}>
                <Collapse>
                  <Panel header={t("order_history")} key={1}>
                    <OrderHistoryTable />
                  </Panel>
                </Collapse>
              </Col>
              <Col span={24}>
                <Collapse>
                  <Panel header={t("order_status")} key={1}>
                    <OrderStatusTable />
                  </Panel>
                </Collapse>
              </Col>
              <Col span={24}>
                <Collapse>
                  <Panel header={t("history_driver")} key={1}>
                    <WorkerHistoryTable orderAssent={true} />
                  </Panel>
                </Collapse>
              </Col>
            </Row>
          </>
        )}

        {/* Payment Confirmation Modal */}
        <Modal
          title={t("confirm_payment_receipt")}
          open={confirmModalVisible}
          onCancel={() => setConfirmModalVisible(false)}
          onOk={async () => {
            await actions.confirmPayment(Number(id));
            successNotification(t("payment_confirmed_successfully"));
            setConfirmModalVisible(false);
          }}
          okText={t("confirm")}
          cancelText={t("cancel")}
          confirmLoading={loading.includes("confirm_payment")}
          width={550}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              padding: "16px 0",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px",
                backgroundColor: token.colorFillAlter,
                borderRadius: token.borderRadius,
                border: `1px solid ${token.colorBorder}`,
              }}
            >
              <span
                style={{ fontSize: "14px", color: token.colorTextSecondary }}
              >
                {t("order_number")}:
              </span>
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: token.colorTextSecondary,
                }}
              >
                {details?.number}
              </span>
            </div>

            <div
              style={{
                padding: "16px",
                backgroundColor: token.colorSuccessBg,
                border: `1px solid ${token.colorSuccessBorder}`,
                borderRadius: token.borderRadius,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "12px",
                }}
              >
                <span
                  style={{ fontSize: "14px", color: token.colorTextSecondary }}
                >
                  {t("total_products_price")}:
                </span>
                <span
                  style={{
                    fontSize: "15px",
                    fontWeight: "500",
                    color: token.colorText,
                  }}
                >
                  {details?.totalPrice
                    ? Number(details.totalPrice).toFixed(2)
                    : "0.00"}{" "}
                  {t("currency")}
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "12px",
                }}
              >
                <span
                  style={{ fontSize: "14px", color: token.colorTextSecondary }}
                >
                  {t("delivery_fee")}:
                </span>
                <span
                  style={{
                    fontSize: "15px",
                    fontWeight: "500",
                    color: token.colorText,
                  }}
                >
                  {details?.priceDeliveryServiceOnClient
                    ? Number(details.priceDeliveryServiceOnClient).toFixed(2)
                    : "0.00"}{" "}
                  {t("currency")}
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "12px",
                }}
              >
                <span
                  style={{ fontSize: "14px", color: token.colorTextSecondary }}
                >
                  {t("delivery_type")}:
                </span>
                <Tag
                  title={
                    details?.isExpressDelivery
                      ? t("express_delivery")
                      : t("normal_delivery")
                  }
                  color={details?.isExpressDelivery ? "blue" : "default"}
                />
              </div>

              <div
                style={{
                  borderTop: `2px solid ${token.colorSuccess}`,
                  marginTop: "16px",
                  paddingTop: "12px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: token.colorText,
                  }}
                >
                  {t("total_amount_received")}:
                </span>
                <span
                  style={{
                    fontSize: "20px",
                    fontWeight: "700",
                    color: token.colorSuccess,
                  }}
                >
                  {details?.finalPrice
                    ? Number(details.finalPrice).toFixed(2)
                    : "0.00"}{" "}
                  {t("currency")}
                </span>
              </div>
            </div>
          </div>
        </Modal>

        <Modal
          title={t("finish_accounting_finalization")}
          open={accountingModalVisible}
          onCancel={() => {
            setAccountingModalVisible(false);
            setAccountingFinishNote("");
          }}
          onOk={async () => {
            if (isCashCheckBlocking || isCashCheckConfirming) return;
            try {
              await actions.markAccountingFinished(Number(id), {
                note: accountingFinishNote || undefined,
              });
              successNotification(t("accounting_finished_successfully"));
              setAccountingModalVisible(false);
              setAccountingFinishNote("");
            } catch (error: any) {
              const errorKey = error?.response?.data?.errorKey;

              if (errorKey === CashCheckErrorKey.ACCOUNTING_ALREADY_FINISHED) {
                setAccountingModalVisible(false);
                setAccountingFinishNote("");
                setCashCheckGate(null);
                await refreshOrderDetailsSilently(true).catch(() => undefined);
                return;
              }

              // Re-fetch both joined sources without resetting page UI state.
              if (errorKey === CashCheckErrorKey.INCOMPLETE) {
                setAccountingModalVisible(false);
                setAccountingFinishNote("");
                setCashCheckGate(null);
                await refreshOrderDetailsSilently();
              }
            }
          }}
          okText={t("confirm")}
          cancelText={t("cancel")}
          confirmLoading={
            isCashCheckConfirming ||
            loading.includes("mark_accounting_finished")
          }
          okButtonProps={{
            disabled: isCashCheckBlocking || isCashCheckConfirming,
          }}
          width={550}
        >
          <div style={{ padding: "12px 0" }}>
            <Input.TextArea
              value={accountingFinishNote}
              rows={4}
              maxLength={1000}
              showCount
              placeholder={t("accounting_finish_note")}
              onChange={(event) => setAccountingFinishNote(event.target.value)}
            />
          </div>
        </Modal>
        <ReturnToCashModal
          open={returnToCashOpen}
          loading={returnToCashLoading}
          isActiveTrip={isTripActive}
          onSubmit={handleReturnToCash}
          onCancel={() => setReturnToCashOpen(false)}
        />
      </PageDetails>
    </>
  );
};

const OrderDetailesPage = () => {
  return (
    <OrderContextProvider>
      <OrderStatusContextProvider>
        <OrderHistoryContextProvider>
          <DeliveryWorkerHistoryContextProvider>
            <StoreContextProvider>
              <Order />
            </StoreContextProvider>
          </DeliveryWorkerHistoryContextProvider>
        </OrderHistoryContextProvider>
      </OrderStatusContextProvider>
    </OrderContextProvider>
  );
};
export default OrderDetailesPage;

//TODO

// "orderProducts": [],
// "reasonCancel": null
